import ExportButton from '../../../components/ExportButton';
import { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import Breadcrumbs from "../../../components/Breadcrumbs";
import NewTableComponent from "../../../components/NewTableComponent";
import DateFilterDropdown from "../../../components/DateFilterDropdown";
import TextFilterDropdown from "../../../components/TextFilterDropdown";
import Stats from "../../../components/Stats";
import {
    intransitSto,
    plusIcon, prospectIcon,
    stockAlertIcon, totalSto, vendorActiveIcon,
    vendorIcon, vendorInActiveIcon, warehouseIcon
} from "../../../utils/svgs";
import { PiPencilSimpleLine, PiTrash } from "react-icons/pi";
import { GrAttachment } from "react-icons/gr";
import { TbLayoutGridAdd } from "react-icons/tb";
import AddVendor from "./AddVendor";
import AddWarehouse from "./AddWarehouse";
import DeleteModal from "../../../components/modals/DeleteModal";
import CustomModal from "../../../components/CustomModal";
import PermissionWrapper from '../../../components/PermissionWrapper';
import { AppMainModules, InventorySubModules, VendorAndWarehousePermission } from '../../../utils/app-constant';
import { hasPermission } from "../../../utils/helper";
import { useDispatch, useSelector } from "react-redux";
import {
    deleteVendor, deleteWarehouse, getVendorsPaginated, getWarehousesPaginated, getVendorOverview, getWarehouseOverview
} from "../../../store/Slices/PartVendorSlice";
import { fetchPartCategoriesDropdown } from "../../../store/Slices/dropdownSlice";
import { apiService } from "../../../services/apiService";
import { errorNotify, successNotify } from "../../../utils/toast";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import MultiLineText from "../../../components/MultiLineText";
import "../../../styles/Services.css";

const normalizeTab = (tab) => (tab === 'warehouse' ? 'warehouse' : 'vendor');

const VendorAndWarehouse = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [searchFilters, setSearchFilters] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [searchText, setSearchText] = useState('');

    const [vendorPdfExportLoading, setVendorPdfExportLoading] = useState(false);
    const [vendorExcelExportLoading, setVendorExcelExportLoading] = useState(false);
    const [warehousePdfExportLoading, setWarehousePdfExportLoading] = useState(false);
    const [warehouseExcelExportLoading, setWarehouseExcelExportLoading] = useState(false);

    const [vendorDropdownOptions, setVendorDropdownOptions] = useState([]);
    const [warehouseDropdownOptions, setWarehouseDropdownOptions] = useState([]);

    const tabFromPath = location.pathname === '/warehouse' ? 'warehouse' : (location.pathname === '/vendor' ? 'vendor' : null);
    const tabFromQuery = new URLSearchParams(location.search).get('tab');
    const tabFromState = location.state?.activeTab;
    const activeTab = tabFromPath || normalizeTab(tabFromQuery || tabFromState);

    // Reset filters on tab change so they don't bleed across tabs
    useEffect(() => {
        setSearchFilters(null);
        setSearchText('');
        setCurrentPage(1);
    }, [activeTab]);

    // Redux State
    const {
        getVendorsPaginatedData,
        getVendorsLoading,
        getWarehousesPaginatedData,
        getWarehousesLoading,
        deleteVendorLoading,
        deleteWarehouseLoading,
        vendorOverview,
        getWarehouseOverviewLoading,
        warehouseOverview
    } = useSelector((state) => state.partVendors);
    const { partCategoriesDropdownData } = useSelector((state) => state.dropdowns);

    // Modal States
    const [showAddEditModal, setShowAddEditModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    // Fetch Dropdowns
    useEffect(() => {
        dispatch(fetchPartCategoriesDropdown());
        apiService.getPartVendorsDropdown().then((res) => {
            const list = res?.data || res || [];
            if (Array.isArray(list)) {
                setVendorDropdownOptions(
                    list.map((item) => ({
                        label: item.label || item.name,
                        value: item.label || item.name,
                    }))
                );
            }
        }).catch(() => { });

        apiService.getWarehousesDropdown().then((res) => {
            const list = res?.data || res || [];
            if (Array.isArray(list)) {
                setWarehouseDropdownOptions(
                    list.map((item) => ({
                        label: item.label || item.name,
                        value: item.label || item.name,
                    }))
                );
            }
        }).catch(() => { });
    }, [dispatch]);

    // Vendor dropdown options with fallback from table results
    const vendorOptions = useMemo(() => {
        if (vendorDropdownOptions && vendorDropdownOptions.length > 0) {
            return vendorDropdownOptions;
        }
        const names = (getVendorsPaginatedData?.results || [])
            .map((item) => item.name)
            .filter(Boolean);
        return Array.from(new Set(names)).map((name) => ({ label: name, value: name }));
    }, [vendorDropdownOptions, getVendorsPaginatedData]);

    // Warehouse dropdown options with fallback from table results
    const warehouseOptions = useMemo(() => {
        if (warehouseDropdownOptions && warehouseDropdownOptions.length > 0) {
            return warehouseDropdownOptions;
        }
        const names = (getWarehousesPaginatedData?.results || [])
            .map((item) => item.name)
            .filter(Boolean);
        return Array.from(new Set(names)).map((name) => ({ label: name, value: name }));
    }, [warehouseDropdownOptions, getWarehousesPaginatedData]);

    // Fetch Data
    useEffect(() => {
        const fetchParams = {
            page: currentPage,
            limit: limit,
            search_text: searchText,
            searchFilters: searchFilters
        };

        if (activeTab === 'vendor') {
            dispatch(getVendorsPaginated(fetchParams));
            dispatch(getVendorOverview());
        } else {
            dispatch(getWarehousesPaginated(fetchParams));
            dispatch(getWarehouseOverview());
        }
    }, [dispatch, activeTab, currentPage, limit, searchFilters, searchText]);

    const hasActiveFilters = useMemo(() => {
        const hasSearchText = Boolean(searchText && String(searchText).trim().length > 0);
        const hasSearchFilters = Boolean(
            searchFilters &&
            typeof searchFilters === "object" &&
            Object.keys(searchFilters).some((key) => {
                const filter = searchFilters[key];
                if (!filter) return false;
                if (Array.isArray(filter.value)) return filter.value.length > 0;
                return filter.value !== undefined && filter.value !== null && filter.value !== "";
            })
        );
        return hasSearchText || hasSearchFilters;
    }, [searchText, searchFilters]);

    const buildExportPayload = () => {
        const payload = {};
        if (searchText && String(searchText).trim() !== "") {
            payload.search_text = String(searchText).trim();
        }
        if (searchFilters && typeof searchFilters === "object") {
            Object.assign(payload, searchFilters);
        }
        return payload;
    };

    const triggerFileDownload = (filePath, defaultName) => {
        if (!filePath) {
            throw new Error("File path was not returned from the server");
        }
        const fileUrl = /^https?:\/\//i.test(filePath) ? filePath : `${process.env.REACT_APP_API_FILES_URL || ""}${filePath}`;
        const link = document.createElement("a");
        link.href = fileUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.setAttribute("download", defaultName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleVendorExportPdf = async () => {
        if (!hasActiveFilters || vendorPdfExportLoading) return;
        setVendorPdfExportLoading(true);
        try {
            const payload = buildExportPayload();
            const response = await apiService.getPartVendorsListingPdf(payload);
            const url = response?.data?.url || response?.url;
            triggerFileDownload(url, "vendors-listing.pdf");
        } catch (err) {
            errorNotify(err?.message || err || "Failed to download Vendor PDF");
        } finally {
            setVendorPdfExportLoading(false);
        }
    };

    const handleVendorExportExcel = async () => {
        if (!hasActiveFilters || vendorExcelExportLoading) return;
        setVendorExcelExportLoading(true);
        try {
            const payload = buildExportPayload();
            const response = await apiService.getPartVendorsListingExcel(payload);
            const url = response?.data?.url || response?.url;
            triggerFileDownload(url, "vendors-listing.xlsx");
        } catch (err) {
            errorNotify(err?.message || err || "Failed to download Vendor Excel");
        } finally {
            setVendorExcelExportLoading(false);
        }
    };

    const handleWarehouseExportPdf = async () => {
        if (!hasActiveFilters || warehousePdfExportLoading) return;
        setWarehousePdfExportLoading(true);
        try {
            const payload = buildExportPayload();
            const response = await apiService.getWarehousesListingPdf(payload);
            const url = response?.data?.url || response?.url;
            triggerFileDownload(url, "warehouses-listing.pdf");
        } catch (err) {
            errorNotify(err?.message || err || "Failed to download Warehouse PDF");
        } finally {
            setWarehousePdfExportLoading(false);
        }
    };

    const handleWarehouseExportExcel = async () => {
        if (!hasActiveFilters || warehouseExcelExportLoading) return;
        setWarehouseExcelExportLoading(true);
        try {
            const payload = buildExportPayload();
            const response = await apiService.getWarehousesListingExcel(payload);
            const url = response?.data?.url || response?.url;
            triggerFileDownload(url, "warehouses-listing.xlsx");
        } catch (err) {
            errorNotify(err?.message || err || "Failed to download Warehouse Excel");
        } finally {
            setWarehouseExcelExportLoading(false);
        }
    };

    const handleAddClick = () => {
        setIsEditMode(false);
        setSelectedItem(null);
        setShowAddEditModal(true);
    };

    const handleEditClick = (item) => {
        if (activeTab === 'vendor') {
            navigate(`/vendor/${item.id}`, { state: { type: 'vendor', data: item } });
        } else {
            setIsEditMode(true);
            setSelectedItem(item);
            setShowAddEditModal(true);
        }
    };

    const handleDeleteClick = (item) => {
        setItemToDelete(item);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            if (activeTab === 'vendor') {
                await dispatch(deleteVendor(itemToDelete.id)).unwrap();
                successNotify("Vendor deleted successfully");
                dispatch(getVendorsPaginated({ page: currentPage, limit, searchFilters, search_text: searchText }));
                dispatch(getVendorOverview());
            } else {
                await dispatch(deleteWarehouse(itemToDelete.id)).unwrap();
                successNotify("Warehouse deleted successfully");
                dispatch(getWarehousesPaginated({ page: currentPage, limit, searchFilters, search_text: searchText }));
            }
            setShowDeleteModal(false);
            setItemToDelete(null);
        } catch (error) {
            errorNotify(error);
        }
    };

    const handleSave = () => {
        setShowAddEditModal(false);
        const fetchParams = {
            page: currentPage,
            limit: limit,
            search_text: searchText,
            searchFilters: searchFilters
        };
        if (activeTab === 'vendor') {
            dispatch(getVendorsPaginated(fetchParams));
            dispatch(getVendorOverview());
        } else {
            dispatch(getWarehousesPaginated(fetchParams));
            dispatch(getWarehouseOverview());
        }
    };

    const handleRowClick = (row) => {
        const type = activeTab === "vendor" ? "vendor" : "warehouse";
        navigate(`/${type}/${row.id}`, { state: { type, data: row } });
    };

    const handleSearch = (text) => {
        setSearchText(text);
        setCurrentPage(1);
    };

    const vendorWarehouseTabs = [
        {
            key: "vendor",
            label: "Vendor",
            permission: VendorAndWarehousePermission.ViewVendor,
            subModule: InventorySubModules.Vendor
        },
        {
            key: "warehouse",
            label: "Warehouse",
            permission: VendorAndWarehousePermission.ViewWarehouse,
            subModule: InventorySubModules.Warehouse
        }
    ].filter(tab => hasPermission(tab.subModule, tab.permission, AppMainModules.inventoryManagement));

    useEffect(() => {
        if (!vendorWarehouseTabs.length) return;
        const isActiveTabAllowed = vendorWarehouseTabs.some((tab) => tab.key === activeTab);
        if (isActiveTabAllowed) return;

        const fallbackTab = vendorWarehouseTabs[0].key;
        navigate(fallbackTab === 'warehouse' ? '/warehouse' : '/vendor', { replace: true });
    }, [activeTab, navigate, vendorWarehouseTabs]);

    const vendorStatsData = [
        { title: "Total Vendor", value: vendorOverview?.total_vendor || 0, text: "Total vendors", icon: vendorIcon },
        { title: "Active", value: vendorOverview?.active?.count || 0, text: "Active vendors", icon: vendorActiveIcon },
        { title: "Inactive", value: vendorOverview?.inactive?.count || 0, text: "Inactive vendors", icon: vendorInActiveIcon },
        { title: "Prospect", value: vendorOverview?.prospect?.count || 0, text: "Prospect vendors", icon: prospectIcon },
    ];

    const warehouseStatsData = [
        { title: "Total Warehouses", value: warehouseOverview?.total_warehouses || 0, text: "Total warehouses", icon: warehouseIcon },
        { title: "Total Stock Alerts", value: warehouseOverview?.total_stock_alerts || 0, text: "Alerts", icon: stockAlertIcon },
        { title: "Total STOs", value: warehouseOverview?.total_sto || 0, text: "STOs", icon: totalSto },
        { title: "In-Transit STOs", value: warehouseOverview?.in_transit_sto || 0, text: "In-Transit", icon: intransitSto },
    ];

    const commonColumns = {
        checked: true,
        disabled: true,
        hide: false,
        exportColumn: true,
        sortable: false,
        tableColumn: true,
    };

    const vendorColumns = [
        {
            ...commonColumns,
            name: "Vendor ID",
            key: "part_vendor_id",
            selector: (row) => (
                <span
                    className="hyperlink"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(row);
                    }}
                >
                    {row.part_vendor_id}
                </span>
            ),
            width: "150px",
            type: "text",
            tableColumn: false
        },
        {
            ...commonColumns,
            name: "Vendor Name",
            key: "name",
            selector: (row) => row.name,
            cell: (row) => <MultiLineText text={row.name || "-"} maxWidth="180px" />,
            wrap: true,
            width: "200px",
            type: "select",
            options: vendorOptions,
            filterName: "nameFilter",
        },
        {
            ...commonColumns,
            name: "Address",
            key: "location",
            selector: (row) => row.location || "-",
            width: "250px",
            wrap: true,
            tableColumn: false,
            cell: (row) => <MultiLineText text={row.location || "-"} maxWidth="220px" />,
            type: "text",
        },
        {
            ...commonColumns,
            name: "Primary POC",
            key: "poc_name",
            width: "150px",
            type: "text",
            filterComponent: TextFilterDropdown,
            filterName: "pocNameFilter",
            selector: (row) => row.poc_name || "-"
        },
        { ...commonColumns, name: "Contact Number", key: "poc_contact", selector: (row) => row.poc_contact || "-", width: "170px", type: "text", filterComponent: TextFilterDropdown, filterName: "contactNumberFilter" },
        { ...commonColumns, name: "Email Address", key: "poc_email", selector: (row) => row.poc_email || "-", width: "220px", type: "text", filterComponent: TextFilterDropdown, filterName: "emailAddressFilter" },
        {
            ...commonColumns,
            name: "Category",
            key: "categories",
            width: "200px",
            type: "select",
            options: partCategoriesDropdownData,
            filterName: "categoryFilter",
            selector: (row) => {
                const items = row.categories || [];

                if (items.length === 0) {
                    return <span className="no-items">No tasks</span>;
                }

                if (items.length === 1) {
                    return (
                        <span className="service-task">
                            <MultiLineText text={items[0]?.title || items[0]?.title} maxWidth={"150px"} />
                        </span>
                    );
                }

                const firstItem = items[0]?.title || items[0]?.title;
                const remainingCount = items.length - 1;

                return (
                    <div className="service-tasks-container">
                        <span className="first-task">
                            <MultiLineText text={firstItem} maxWidth={"150px"} />
                        </span>

                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip>
                                    <div>
                                        {items.slice(1).map((item, idx) => (
                                            <div key={idx} className='tooltip_item'>{item?.title}</div>
                                        ))}
                                    </div>
                                </Tooltip>
                            }
                        >
                            <span className="additional-tasks">+{remainingCount}</span>
                        </OverlayTrigger>
                    </div>
                );
            },
        },
        {
            ...commonColumns, name: "Status", key: "status", width: "150px", type: "select", filterName: "statusFilter",
            options: [
                { label: 'Active', value: 'active' },
                { label: 'Inactive', value: 'inactive' },
            ],
            selector: (row) => (
                <div className="d-flex align-items-center gap-2">
                    <span style={{
                        color: (row.status || '').toLowerCase() === 'active' ? '#28A745' : '#DC3545',
                        fontWeight: '500',
                        textTransform: 'capitalize'
                    }}>{row.status}</span>
                </div>
            )
        },
        {
            ...commonColumns,
            name: "Created Date",
            key: "created_at",
            width: "170px",
            type: "select",
            filterComponent: DateFilterDropdown,
            filterName: "createdAtFilter",
            selector: (row) => row.created_at ? moment(row.created_at).format("DD-MM-YYYY") : "-",
        },
        {
            ...commonColumns, name: "Action", key: "action", width: "150px", tableColumn: false, ignoreRowClick: true,
            selector: (row) => (
                <div className="d-flex align-items-center gap-3">
                    <span style={{ cursor: 'pointer', color: '#666', display: 'flex', alignItems: 'center', gap: '2px' }}>
                    </span>
                    <PermissionWrapper
                        module={InventorySubModules.Vendor}
                        permission={VendorAndWarehousePermission.UpdateVendor}
                        mainModule={AppMainModules.inventoryManagement}
                    >
                        <span onClick={() => handleEditClick(row)} style={{ cursor: 'pointer', color: '#666' }}><PiPencilSimpleLine size={18} /></span>
                    </PermissionWrapper>
                    <PermissionWrapper
                        module={InventorySubModules.Vendor}
                        permission={VendorAndWarehousePermission.DeleteVendor}
                        mainModule={AppMainModules.inventoryManagement}
                    >
                        <span onClick={() => handleDeleteClick(row)} style={{ cursor: 'pointer', color: '#FA6262' }}><PiTrash size={18} /></span>
                    </PermissionWrapper>
                </div>
            )
        },
    ];

    const warehouseColumns = [
        {
            ...commonColumns,
            name: "Warehouse ID",
            key: "warehouse_id",
            selector: (row) => row.warehouse_id,
            cell: (row) => (
                <span
                    className="hyperlink"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(row);
                    }}
                >
                    {row.warehouse_id}
                </span>
            ),
            width: "150px",
            type: "text",
            tableColumn: false,
        },
        {
            ...commonColumns,
            name: "Warehouse Name",
            key: "name",
            selector: (row) => row.name,
            cell: (row) => <MultiLineText text={row.name || "-"} maxWidth="180px" />,
            wrap: true,
            width: "200px",
            type: "select",
            options: warehouseOptions,
            filterName: "nameFilter",
        },
        {
            ...commonColumns,
            name: "Address",
            key: "location",
            selector: (row) => row.location || "-",
            wrap: true,
            cell: (row) => <MultiLineText text={row.location || "-"} maxWidth="220px" />,
            width: "250px",
            type: "text",
            filterComponent: TextFilterDropdown,
            filterName: "addressFilter",
        },
        {
            ...commonColumns,
            name: "Primary POC",
            key: "poc_name",
            width: "150px",
            type: "text",
            filterComponent: TextFilterDropdown,
            filterName: "pocNameFilter",
            selector: (row) => row.poc_name || "-",
        },
        {
            ...commonColumns,
            name: "Contact Number",
            key: "poc_contact",
            selector: (row) => row.poc_contact || "-",
            width: "170px",
            type: "text",
            filterComponent: TextFilterDropdown,
            filterName: "contactNumberFilter",
        },
        {
            ...commonColumns,
            name: "Email Address",
            key: "poc_email",
            selector: (row) => row.poc_email || "-",
            width: "220px",
            type: "text",
            filterComponent: TextFilterDropdown,
            filterName: "emailAddressFilter",
        },
        {
            ...commonColumns,
            name: "Status",
            key: "status",
            width: "140px",
            type: "select",
            filterName: "statusFilter",
            options: [
                { label: 'Active', value: 'active' },
                { label: 'Inactive', value: 'inactive' },
            ],
            selector: (row) => (
                <div className="d-flex align-items-center gap-2">
                    <span style={{
                        color: (row.status || '').toLowerCase() === 'active' ? '#28A745' : '#DC3545',
                        fontWeight: '500',
                        textTransform: 'capitalize'
                    }}>{row.status || "-"}</span>
                </div>
            )
        },
        {
            ...commonColumns,
            name: "Created Date",
            key: "created_at",
            width: "170px",
            type: "select",
            filterComponent: DateFilterDropdown,
            filterName: "createdAtFilter",
            selector: (row) => row.created_at ? moment(row.created_at).format("DD-MM-YYYY") : "-",
        },
        {
            ...commonColumns, name: "Action", key: "action", width: "150px", tableColumn: false, ignoreRowClick: true,
            selector: (row) => (
                <div className="d-flex align-items-center gap-3">
                    <PermissionWrapper
                        module={InventorySubModules.Warehouse}
                        permission={VendorAndWarehousePermission.UpdateWarehouse}
                        mainModule={AppMainModules.inventoryManagement}
                    >
                        <span onClick={() => handleRowClick(row)} style={{ cursor: 'pointer', color: '#666' }}><PiPencilSimpleLine size={18} /></span>
                    </PermissionWrapper>
                    <PermissionWrapper
                        module={InventorySubModules.Warehouse}
                        permission={VendorAndWarehousePermission.DeleteWarehouse}
                        mainModule={AppMainModules.inventoryManagement}
                    >
                        <span onClick={() => handleDeleteClick(row)} style={{ cursor: 'pointer', color: '#FA6262' }}><PiTrash size={18} /></span>
                    </PermissionWrapper>
                </div>
            )
        },
    ];

    const columns = activeTab === 'vendor' ? vendorColumns : warehouseColumns;
    const items = activeTab === 'vendor' ? (getVendorsPaginatedData?.results || []) : (getWarehousesPaginatedData?.results || []);
    const meta = activeTab === 'vendor' ? getVendorsPaginatedData?.meta : getWarehousesPaginatedData?.meta;
    const isLoading = activeTab === 'vendor' ? getVendorsLoading : getWarehousesLoading;
    const pageHeading = activeTab === "vendor" ? "Vendors" : "Warehouses";

    const processedResults = items.map((item, idx, arr) => {
        const isLastFour = idx >= arr.length - 4;
        return { ...item, isLastFourServiceRow: isLastFour };
    });

    return (
        <div>
            <Breadcrumbs values={['Vendor & Warehouse', (activeTab === "vendor" ? 'Vendor' : 'Warehouse')]} />

            <div className='access_view'>
                <h2>{pageHeading}</h2>

                <div className='services_buttons'>
                    {activeTab === "vendor" ? (
                        <>
                            <ExportButton
                                type="pdf"
                                onClick={handleVendorExportPdf}
                                disabled={!hasActiveFilters || vendorPdfExportLoading}
                                loading={vendorPdfExportLoading}
                                hasActiveFilters={hasActiveFilters}
                            />

                            <ExportButton
                                type="excel"
                                onClick={handleVendorExportExcel}
                                disabled={!hasActiveFilters || vendorExcelExportLoading}
                                loading={vendorExcelExportLoading}
                                hasActiveFilters={hasActiveFilters}
                            />

                            <PermissionWrapper
                                module={InventorySubModules.Vendor}
                                permission={VendorAndWarehousePermission.CreateVendor}
                                mainModule={AppMainModules.inventoryManagement}
                            >
                                <button onClick={handleAddClick}>{plusIcon} Register Vendor</button>
                            </PermissionWrapper>
                        </>
                    ) : (
                        <>
                            <ExportButton
                                type="pdf"
                                onClick={handleWarehouseExportPdf}
                                disabled={!hasActiveFilters || warehousePdfExportLoading}
                                loading={warehousePdfExportLoading}
                                hasActiveFilters={hasActiveFilters}
                            />

                            <ExportButton
                                type="excel"
                                onClick={handleWarehouseExportExcel}
                                disabled={!hasActiveFilters || warehouseExcelExportLoading}
                                loading={warehouseExcelExportLoading}
                                hasActiveFilters={hasActiveFilters}
                            />

                            <PermissionWrapper
                                module={InventorySubModules.Warehouse}
                                permission={VendorAndWarehousePermission.CreateWarehouse}
                                mainModule={AppMainModules.inventoryManagement}
                            >
                                <button onClick={handleAddClick}>{plusIcon} Register Warehouse</button>
                            </PermissionWrapper>
                        </>
                    )}
                </div>
            </div>

            <Stats statsData={activeTab === "vendor" ? vendorStatsData : warehouseStatsData} />

            <NewTableComponent
                showAddBtn={false}
                columns={columns}
                data={processedResults}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                limit={limit}
                setLimit={setLimit}
                totalItems={meta?.totalItems || 0}
                totalPages={meta?.totalPages || 0}
                onSearch={handleSearch}
                loading={isLoading}
                setSearchFilters={setSearchFilters}
                searchFilters={searchFilters}
                paginationLoading={isLoading}
                showFilter={true}
                showPagination={true}
                pageName="VendorAndWarehouse"
            />

            <CustomModal
                show={showAddEditModal}
                handleClose={() => setShowAddEditModal(false)}
                heading={
                    activeTab === "vendor"
                        ? (isEditMode ? "Edit Vendor" : "Register Vendor")
                        : (isEditMode ? "Edit Warehouse" : "Register Warehouse")
                }
                size="lg"
            >
                {activeTab === "vendor" ? (
                    <AddVendor
                        onSave={handleSave}
                        onCancel={() => setShowAddEditModal(false)}
                    />
                ) : (
                    <AddWarehouse
                        onSave={handleSave}
                        onCancel={() => setShowAddEditModal(false)}
                    />
                )}
            </CustomModal>

            <DeleteModal
                open={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onLogout={handleDeleteConfirm}
                topHeading={
                    activeTab === "vendor"
                        ? "Are you sure you want to Delete this Vendor?"
                        : "Are you sure you want to Delete this Warehouse?"
                }
                description=""
                subDescription={
                    activeTab === "vendor"
                        ? "Deleting this Vendor will permanently remove all associated information. This action cannot be undone."
                        : "Deleting this Warehouse will permanently remove all associated information. This action cannot be undone."
                }
                loading={activeTab === 'vendor' ? deleteVendorLoading : deleteWarehouseLoading}
            />
        </div>
    );
};

export default VendorAndWarehouse;
